/**
 * "Advanced privacy" inside the donor sheet: the one place a donor can become **anonymous to all
 * organizations** (GH-858, DEC-049).
 *
 * Owner, 2026-10-03: "Anonymous" on a gift hides a donor from the public, not from the
 * organization. The rare donor who wants to be hidden from organizations too gets a setting deep
 * in the profile, with a clear explanation — and it must not be easy to reach or to flip by
 * accident, because a mistaken flip would scare off partner organizations.
 *
 * So, by construction:
 *
 *  - **Deep.** The only way in is the sheet's profile → "Edit my details" → "Advanced privacy".
 *    Nothing on the give path, the profile's first screen or sign-in links here.
 *  - **Three deliberate steps on.** "Turn on" opens the explanation (every consequence, one per
 *    line); "Continue" opens a confirmation that states the exact consequence; only its button calls
 *    the server. The safe choice is the prominent one at both steps.
 *  - **One step off.** Turning it off is a single confirmation — the direction that cannot hurt
 *    anybody should not be hard.
 *  - **Plain state, no nagging.** This screen says "On" or "Off" in a sentence. No other screen
 *    mentions it, on or off.
 *
 * The server carries the same guard: `PUT /v1/donor/organization-visibility` refuses a body
 * without the direction's exact confirmation phrase, which `setOrganizationVisibility`'s type
 * makes unwritable here.
 */
import { useEffect, useRef, useState } from "react";
import { ANONYMITY_COPY, type OrganizationVisibilityResponse } from "@givehere/contracts";
import {
  DonorApiError,
  fetchOrganizationVisibility,
  setOrganizationVisibility,
} from "./donor-session.js";
import { Explain } from "./Explain.js";
import { InviterCreditSetting } from "./InviterCreditSetting.js";

type Stage = "status" | "explain" | "confirm-hide" | "confirm-show";

const C = ANONYMITY_COPY;

export function AdvancedPrivacyStep({ token, onBack }: { token: string; onBack: () => void }) {
  const [visibility, setVisibility] = useState<OrganizationVisibilityResponse | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [stage, setStage] = useState<Stage>("status");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetchOrganizationVisibility(token)
      .then((loaded) => {
        if (!cancelled) setVisibility(loaded);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Each step is a new screen inside the same sheet: say so to a screen reader by moving focus to
  // its heading, the way a navigation would.
  useEffect(() => {
    headingRef.current?.focus();
  }, [stage]);

  const go = (next: Stage) => {
    setError(null);
    setStage(next);
  };

  /** The only call that changes anything, reachable only from a confirmation step's button. */
  const commit = (hide: boolean) => {
    setSaving(true);
    setError(null);
    setOrganizationVisibility(
      token,
      hide
        ? { hiddenFromOrganizations: true, confirm: "hide-from-all-organizations" }
        : { hiddenFromOrganizations: false, confirm: "show-to-organizations" },
    )
      .then((saved) => {
        setVisibility(saved);
        setStage("status");
      })
      .catch((caught: unknown) =>
        setError(caught instanceof DonorApiError ? caught.message : "That didn't save. Try again."),
      )
      .finally(() => setSaving(false));
  };

  const failure = error && (
    <p className="notice notice--error" role="alert">
      {error}
    </p>
  );

  if (stage === "explain") {
    return (
      <>
        <h2 id="donor-title" className="donor__title" ref={headingRef} tabIndex={-1}>
          {C.organizationsLabel}
        </h2>
        <ul className="privacy-points">
          {C.organizationsPoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
        <button type="button" className="pay" onClick={() => go("status")}>
          {C.organizationsCancel}
        </button>
        <button
          type="button"
          className="pay pay--outline privacy-next"
          onClick={() => go("confirm-hide")}
        >
          {C.organizationsContinue}
        </button>
      </>
    );
  }

  if (stage === "confirm-hide") {
    return (
      <>
        <h2 id="donor-title" className="donor__title" ref={headingRef} tabIndex={-1}>
          {C.organizationsConfirmHideTitle}
        </h2>
        <p className="privacy-consequence">{C.organizationsConfirmHideBody}</p>
        <button type="button" className="pay" disabled={saving} onClick={() => go("status")}>
          {C.organizationsCancel}
        </button>
        <button
          type="button"
          className="pay pay--outline privacy-next"
          disabled={saving}
          onClick={() => commit(true)}
        >
          {saving ? "Saving…" : C.organizationsConfirmHide}
        </button>
        {failure}
      </>
    );
  }

  if (stage === "confirm-show") {
    return (
      <>
        <h2 id="donor-title" className="donor__title" ref={headingRef} tabIndex={-1}>
          {`${C.organizationsConfirmShow}?`}
        </h2>
        <p className="privacy-consequence">{C.organizationsConfirmShowBody}</p>
        <button type="button" className="pay" disabled={saving} onClick={() => commit(false)}>
          {saving ? "Saving…" : C.organizationsConfirmShowButton}
        </button>
        <button type="button" className="link" disabled={saving} onClick={() => go("status")}>
          {C.organizationsKeepOn}
        </button>
        {failure}
      </>
    );
  }

  const isHidden = visibility?.hiddenFromOrganizations ?? false;
  return (
    <>
      <h2 id="donor-title" className="donor__title" ref={headingRef} tabIndex={-1}>
        {C.advancedPrivacyLabel}
      </h2>

      {visibility === null && !loadFailed && <p className="notice">Loading…</p>}
      {loadFailed && (
        <p className="notice notice--error" role="alert">
          This setting didn't load.
        </p>
      )}

      {visibility !== null && (
        <section className="privacy-setting" aria-labelledby="privacy-setting-title">
          <h3 id="privacy-setting-title" className="paymethods__title">
            {C.organizationsLabel}
          </h3>
          <p className="privacy-setting__state">
            {isHidden ? C.organizationsStateOn : C.organizationsStateOff}
          </p>
          <Explain
            className="privacy-setting__more"
            label="What changes when it's on"
            title={C.organizationsExplanationTitle}
          >
            <p>{C.organizationsExplanation}</p>
          </Explain>
          <button
            type="button"
            className="link"
            onClick={() => go(isHidden ? "confirm-show" : "explain")}
          >
            {isHidden ? C.organizationsTurnOff : C.organizationsTurnOn}
          </button>
        </section>
      )}

      {/* GH-864e: a member who joined by invite; hidden while membership is off. */}
      <InviterCreditSetting token={token} />

      <div className="donor__actions">
        <button type="button" className="link" onClick={onBack}>
          Back
        </button>
      </div>
    </>
  );
}
